/**
 * House Property Type Handler
 */

import { PropertyType } from '../../../shared/definitions/property';
import { ResidentialPropertyHandler } from '../base/residential-property-handler.abstract';
import { RoomTypeConfig, FeatureConfig, FeatureCategory } from '@core/services/property-analysis/property-room-config.service';

export class HouseHandler extends ResidentialPropertyHandler {
  readonly propertyType = PropertyType.House;
  readonly displayName = 'House';
  readonly pluralName = 'Houses';
  readonly icon = 'home';
  readonly description = 'Detached or semi-detached residential house';

  getRoomTypes(): RoomTypeConfig[] {
    const coreRooms = this.getCoreResidentialRoomTypes();
    
    // Houses have additional rooms compared to apartments
    const houseSpecificRooms: RoomTypeConfig[] = [
      {
        typeKey: 'living_room',
        label: 'properties.roomTypes.living_room.label',
        pluralLabel: 'properties.roomTypes.living_room.plural',
        countField: 'livingRooms',
        icon: 'weekend',
        defaultCount: 0,
        description: 'properties.roomTypes.living_room.description',
        formFields: [
          { name: 'name', label: 'properties.fields.roomName', type: 'text', placeholder: 'properties.fieldPlaceholders.mainLivingRoom' },
          { name: 'sqm', label: 'properties.fields.surfaceArea', type: 'number', suffix: 'm²', min: 10, max: 200 },
          { name: 'hasFireplace', label: 'properties.fields.hasFireplace', type: 'checkbox', default: false },
          { name: 'hasBalconyAccess', label: 'properties.fields.balconyAccess', type: 'checkbox', default: false },
          { name: 'floorType', label: 'properties.fields.floorType', type: 'select', options: [
            { value: 'hardwood', label: 'properties.fieldOptions.floorType.hardwood' },
            { value: 'tile', label: 'properties.fieldOptions.floorType.tile' },
            { value: 'carpet', label: 'properties.fieldOptions.floorType.carpet' }
          ]},
          { name: 'notes', label: 'properties.fields.notes', type: 'textarea' }
        ]
      },
      {
        typeKey: 'kitchen',
        label: 'properties.roomTypes.kitchen.label',
        pluralLabel: 'properties.roomTypes.kitchen.plural',
        countField: 'kitchens',
        icon: 'kitchen',
        defaultCount: 0,
        description: 'properties.roomTypes.kitchen.descriptionAlt',
        formFields: [
          { name: 'name', label: 'properties.fields.kitchenName', type: 'text', placeholder: 'properties.fieldPlaceholders.mainKitchen' },
          { name: 'sqm', label: 'properties.fields.surfaceArea', type: 'number', suffix: 'm²', min: 5, max: 100 },
          { name: 'kitchenType', label: 'properties.fields.kitchenType', type: 'select', required: true, options: [
            { value: 'open', label: 'properties.fieldOptions.kitchenType.open' },
            { value: 'semi-open', label: 'properties.fieldOptions.kitchenType.semi_open' },
            { value: 'closed', label: 'properties.fieldOptions.kitchenType.closedSimple' }
          ]},
          { name: 'isEquipped', label: 'properties.fields.fullyEquipped', type: 'checkbox', default: false },
          { name: 'hasIsland', label: 'properties.fields.hasIsland', type: 'checkbox', default: false },
          { name: 'hasDishwasher', label: 'properties.fields.dishwasher', type: 'checkbox', default: false },
          { name: 'hasOven', label: 'properties.fields.oven', type: 'checkbox', default: false },
          { name: 'notes', label: 'properties.fields.notes', type: 'textarea' }
        ]
      }
    ];

    return [...coreRooms, ...houseSpecificRooms];
  }

  getFeatures(): FeatureConfig[] {
    const coreFeatures = this.getCoreResidentialFeatures();
    
    // Houses can have all residential features including pool and garden
    const houseSpecificFeatures: FeatureConfig[] = [
      {
        key: 'basement',
        label: 'properties.featureTypes.basement.label',
        icon: 'stairs',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'properties.featureTypes.basement.description',
        detailsInterface: 'BasementDetail',
        formFields: [
          { name: 'sqm', label: 'properties.fields.surfaceArea', type: 'number', suffix: 'm²', required: true, min: 5 },
          { name: 'heightCm', label: 'properties.fields.height', type: 'number', suffix: 'cm', min: 150, max: 400 },
          { name: 'hasWindows', label: 'properties.fields.hasWindows', type: 'checkbox', default: false },
          { name: 'hasElectricity', label: 'properties.fields.electricity', type: 'checkbox', default: false },
          { name: 'hasHeating', label: 'properties.fields.heating', type: 'checkbox', default: false },
          { name: 'isFinished', label: 'properties.fields.finishedHabitable', type: 'checkbox', default: false }
        ]
      },
      {
        key: 'attic',
        label: 'properties.featureTypes.attic.label',
        icon: 'roofing',
        category: FeatureCategory.PROPERTY_ONLY,
        description: 'properties.featureTypes.attic.description',
        detailsInterface: 'AtticDetail',
        formFields: [
          { name: 'sqm', label: 'properties.fields.surfaceArea', type: 'number', suffix: 'm²', required: true, min: 5 },
          { name: 'heightCm', label: 'properties.fields.height', type: 'number', suffix: 'cm', min: 100, max: 400 },
          { name: 'isConvertible', label: 'properties.fields.convertibleToLivingSpace', type: 'checkbox', default: false },
          { name: 'hasWindows', label: 'properties.fields.hasWindows', type: 'checkbox', default: false },
          { name: 'hasElectricity', label: 'properties.fields.electricity', type: 'checkbox', default: false }
        ]
      }
    ];

    return [...coreFeatures, ...houseSpecificFeatures];
  }

  override getRecommendedMinSurface(): number {
    return 80;
  }

  override getPriceRangeMultiplier(): number {
    return 1.3;
  }
}

